import { useMemo } from "react";
import { useQuery } from "react-query";
import { getDiscount, getDiscountBySKU, getPrice, getStockStatus, magento, trialMagento, } from "@jti/magento";
import { getConditionalPurchaseOffer } from "../../hooks/useSpecialPromo";
import { getOptions, getPriceTiers, getSingleProductPrice } from "../../utils";
const getMultipacksCount = (productData) => {
    const QtyOfMultiItems = productData?.items
        ?.filter(({ type }) => type === "multi")
        .map(({ option_id, qty }) => ({ option_id, qty }));
    return QtyOfMultiItems || [];
};
const getDefaultPriceTier = (priceInformation) => ({
    final_price: priceInformation.finalPrice,
    total_price: priceInformation.finalPrice,
    quantity: 1,
    discount: {},
});
const getSiblingItemsStockStatus = (stockStatusesData) => {
    const stockStatuses = {};
    stockStatusesData?.products?.items.forEach((item) => {
        stockStatuses[item.sku] = item?.stock_status;
    });
    return stockStatuses;
};
export const useProductData = ({ sku, pageSize = 1, isTrial = false, currentVariantSku, categoriesSKUList = [], isDynamicGalleryEnabled = false, }) => {
    const magentoClient = isTrial ? trialMagento : magento;
    const { data: getProductsResponseData, isFetching, isSuccess, } = useQuery({
        queryKey: ["PRODUCT_DETAILS", sku, currentVariantSku, pageSize, { isTrial }],
        queryFn: () => magentoClient.getProductsDetailsBySku([sku], pageSize),
    });
    const { data: siblingItemsStockStatus = {}, isFetching: isFetchingSiblingItemsStockStatus } = useQuery({
        queryKey: ["STOCK_STATUSES", ...categoriesSKUList, { isTrial }],
        queryFn: async () => {
            const stockStatusesResponse = await magentoClient.getStockStatuses(categoriesSKUList, categoriesSKUList.length);
            return getSiblingItemsStockStatus(stockStatusesResponse);
        },
        enabled: categoriesSKUList.length > 0,
    });
    const [productData] = getProductsResponseData?.products?.items ?? [];
    const productType = productData?.__typename;
    const isBundle = productType === "BundleProduct";
    const isConfigurableProduct = productType === "ConfigurableProduct";
    const multipacksQuantities = useMemo(() => getMultipacksCount(productData), [productData]);
    const priceInformation = useMemo(() => getPrice(sku, currentVariantSku, productData), [productData, sku, currentVariantSku]);
    const finalPrices = useMemo(() => {
        return productData?.price_range.minimum_price.final_prices ?? [];
    }, [productData]);
    const elementsStockStatus = useMemo(() => isConfigurableProduct || isBundle
        ? getOptions(productData, "stock")
        : {}, [productData]);
    const elementsDynamicGalleryUrls = useMemo(() => (productData && isDynamicGalleryEnabled && isBundle ? getOptions(productData, "gallery") : {}), [productData]);
    const isInStock = useMemo(() => getStockStatus(currentVariantSku, productData), [productData]);
    const discount = useMemo(() => isConfigurableProduct
        ? getDiscountBySKU(currentVariantSku, productData)
        : getDiscount(productData), [productData, isConfigurableProduct, currentVariantSku]);
    const priceTiers = useMemo(() => {
        return [getDefaultPriceTier(priceInformation), ...getPriceTiers(productData)];
    }, [productData]);
    const singleProductPrice = useMemo(() => getSingleProductPrice(productData), [productData]);
    const specialOffer = useMemo(() => getConditionalPurchaseOffer(productData?.promotionalOffers), [productData]);
    const flavor = useMemo(() => {
        return productData?.flavor ?? "";
    }, [productData]);
    const color = useMemo(() => {
        return productData?.color ?? "";
    }, [productData]);
    const subtitle = productData?.subtitle ?? "";
    const productDisclaimer = productData?.product_disclaimer ?? "";
    return {
        isProductDataLoading: isFetching,
        isProductDataLoaded: !isFetching && isSuccess,
        elementsStockStatus,
        siblingItemsStockStatus,
        isFetchingSiblingItemsStockStatus,
        isInStock,
        priceInformation,
        discount,
        priceTiers,
        singleProductPrice,
        elementsDynamicGalleryUrls,
        productType,
        specialOffer,
        multipacksQuantities,
        finalPrices,
        subtitle,
        productDisclaimer,
        color,
        flavor,
    };
};
//# sourceMappingURL=useProductData.js.map